import { createSelector } from 'reselect'
import { propOr, pathOr } from 'ramda'

const getReporting = state => state.reporting

// Access selectors
export const selectReportingAccess = createSelector(
  getReporting,
  propOr(null, 'access')
)

export const selectHasReportingAccess = createSelector(
  selectReportingAccess,
  access => access?.hasAccess === true
)

export const selectIsLoadingReportingAccess = createSelector(
  getReporting,
  propOr(false, 'isLoadingAccess')
)

export const selectReportingAccessError = createSelector(
  getReporting,
  propOr(null, 'accessError')
)

// Current report selectors
export const selectCurrentReport = createSelector(
  getReporting,
  propOr(null, 'currentReport')
)

export const selectIsLoadingReport = createSelector(
  getReporting,
  propOr(false, 'isLoadingReport')
)

export const selectReportError = createSelector(
  getReporting,
  propOr(null, 'reportError')
)

export const selectIsReportCompleted = createSelector(
  selectCurrentReport,
  report => report?.isCompleted === true
)

export const selectIsReportProcessing = createSelector(
  selectCurrentReport,
  report => report?.isProcessing === true || report?.isPending === true
)

export const selectIsReportFailed = createSelector(
  selectCurrentReport,
  report => report?.isFailed === true
)

export const selectReportAnswers = createSelector(
  selectCurrentReport,
  report => report?.answers || []
)

export const selectReportAnswersByCategory = createSelector(
  selectCurrentReport,
  report => report?.answersByCategory || {}
)

export const selectReportVersion = createSelector(
  selectCurrentReport,
  report => report?.version || '1.0.0'
)

export const selectReportMetadata = createSelector(
  selectCurrentReport,
  report => report?.metadata || {}
)

// Reports list selectors
export const selectReports = createSelector(
  getReporting,
  propOr([], 'reports')
)

export const selectIsLoadingReports = createSelector(
  getReporting,
  propOr(false, 'isLoadingReports')
)

export const selectReportsError = createSelector(
  getReporting,
  propOr(null, 'reportsError')
)

export const selectReportsMeta = createSelector(
  getReporting,
  propOr(null, 'reportsMeta')
)

// Trigger selectors
export const selectIsTriggering = createSelector(
  getReporting,
  propOr(false, 'isTriggering')
)

export const selectTriggerError = createSelector(
  getReporting,
  propOr(null, 'triggerError')
)

// Polling selectors
export const selectIsPolling = createSelector(
  getReporting,
  propOr(false, 'isPolling')
)

// Combined loading state
export const selectIsReportingLoading = createSelector(
  selectIsLoadingReportingAccess,
  selectIsLoadingReport,
  selectIsTriggering,
  selectIsPolling,
  (loadingAccess, loadingReport, triggering, polling) =>
    loadingAccess || loadingReport || triggering || polling
)

// Helper selector for checking if we can trigger a new report
export const selectCanTriggerReport = createSelector(
  selectHasReportingAccess,
  selectIsReportProcessing,
  selectIsTriggering,
  selectIsPolling,
  (hasAccess, isProcessing, isTriggering, isPolling) =>
    hasAccess && !isProcessing && !isTriggering && !isPolling
)

// Update answer selectors
export const selectIsUpdatingAnswer = createSelector(
  getReporting,
  propOr(false, 'isUpdatingAnswer')
)

export const selectEditingQuestionId = createSelector(
  getReporting,
  propOr(null, 'editingQuestionId')
)

export const selectUpdateAnswerError = createSelector(
  getReporting,
  propOr(null, 'updateAnswerError')
)

// Regenerate answer selectors
export const selectIsRegeneratingAnswer = createSelector(
  getReporting,
  propOr(false, 'isRegeneratingAnswer')
)

export const selectRegeneratingQuestionId = createSelector(
  getReporting,
  propOr(null, 'regeneratingQuestionId')
)

export const selectRegenerateAnswerError = createSelector(
  getReporting,
  propOr(null, 'regenerateAnswerError')
)

// Clone report selectors
export const selectIsCloning = createSelector(
  getReporting,
  propOr(false, 'isCloning')
)

export const selectCloneError = createSelector(
  getReporting,
  propOr(null, 'cloneError')
)

// Export PDF selectors
export const selectIsExportingPdf = createSelector(
  getReporting,
  propOr(false, 'isExportingPdf')
)

export const selectExportPdfError = createSelector(
  getReporting,
  propOr(null, 'exportPdfError')
)

// Export Word selectors
export const selectIsExportingWord = createSelector(
  getReporting,
  propOr(false, 'isExportingWord')
)

export const selectExportWordError = createSelector(
  getReporting,
  propOr(null, 'exportWordError')
)

// Export CSV selectors
export const selectIsExportingCsv = createSelector(
  getReporting,
  propOr(false, 'isExportingCsv')
)

export const selectExportCsvError = createSelector(
  getReporting,
  propOr(null, 'exportCsvError')
)

// Particulars selectors
export const selectReportParticulars = (state) => state.reporting.currentReport?.particulars ?? null
export const selectIsUpdatingParticulars = (state) => state.reporting.isUpdatingParticulars ?? false

